import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Search, Trash2 } from "lucide-react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { toast } from "sonner";
import { trpc } from "@/trpc/trpc";
import { TreatmentDialog } from "@/components/treatment-dialog";
import { PAYMENT_METHOD_LABELS } from "@/lib/payment-methods";
import { Pagination } from "@/components/pagination";

export const Route = createFileRoute("/_authenticated/treatments")({
  component: TreatmentsPage,
});

function formatPrice(cents: number) {
  return `${(cents / 100).toFixed(2)} zł`;
}

const statusConfig = {
  active: { label: "Aktywny", variant: "default" as const },
  inactive: { label: "Nieaktywny", variant: "secondary" as const },
  draft: { label: "Szkic", variant: "outline" as const },
};

type Treatment = {
  id: string;
  name: string;
  description: string | null;
  price: number;
  durationMinutes: number | null;
  status: "active" | "inactive" | "draft";
  sortOrder: number;
};

function TreatmentsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const { data, isLoading } = trpc.treatment.list.useQuery(
    { page, limit: 20, search: debouncedSearch || undefined },
    { placeholderData: (prev) => prev },
  );
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Treatment | null>(null);

  const updateStatus = trpc.treatment.update.useMutation({
    onSuccess: () => utils.treatment.list.invalidate(),
  });

  const deleteTreatment = trpc.treatment.delete.useMutation({
    onSuccess: () => {
      utils.treatment.list.invalidate();
      toast.success("Zabieg usunięty");
    },
    onError: (err) => toast.error(err.message),
  });

  const nextStatus: Record<string, "active" | "inactive" | "draft"> = {
    active: "inactive",
    inactive: "active",
    draft: "active",
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Zabiegi</h1>
          <p className="text-sm text-muted-foreground">
            {data?.total ?? 0} zabiegów
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Dodaj zabieg
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Szukaj zabiegu po nazwie..."
          className="pl-8"
          autoComplete="off"
        />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Nazwa</th>
                <th className="px-4 py-3 font-medium">Cena</th>
                <th className="px-4 py-3 font-medium">Czas</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((t) => {
                const cfg = statusConfig[t.status] ?? statusConfig.draft;
                return (
                  <tr key={t.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">
                      {t.name}
                      {t.paymentMethods && (
                        <Badge variant="outline" className="ml-2 font-normal">
                          {t.paymentMethods.length === 0
                            ? "Brak dostępnych metod płatności"
                            : t.paymentMethods.length === 1 &&
                          t.paymentMethods[0] === "onsite"
                            ? "Płatność tylko w salonie"
                            : `Płatność: ${t.paymentMethods
                                .map((m) => PAYMENT_METHOD_LABELS[m])
                                .join(", ")}`}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatPrice(t.price)}</td>
                    <td className="px-4 py-3">
                      {t.durationMinutes ? `${t.durationMinutes} min` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={cfg.variant}
                        className="cursor-pointer"
                        onClick={() =>
                          updateStatus.mutate({
                            id: t.id,
                            status: nextStatus[t.status],
                          })
                        }
                      >
                        {cfg.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(t);
                            setDialogOpen(true);
                          }}
                        >
                          Edytuj
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm("Usunąć zabieg?")) {
                              deleteTreatment.mutate({ id: t.id });
                            }
                          }}
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    {debouncedSearch
                      ? `Brak zabiegów pasujących do „${debouncedSearch}”`
                      : "Brak zabiegów — dodaj pierwszy zabieg"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination page={page} totalPages={data?.totalPages ?? 1} onPageChange={setPage} />
        </div>
      </Card>

      <TreatmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        treatment={editing}
        onCreated={(created) => setEditing(created)}
      />
    </div>
  );
}
